import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import {
  Field,
  RadioCard,
  Select,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { FilePlus2, RefreshCcw } from 'lucide-react';

export type InsuranceType = 'new' | 'renewal';

export type CongenereOption = {
  value: string;
  label: string;
};

type Props = {
  insuranceType: InsuranceType;
  onInsuranceTypeChange: (value: InsuranceType) => void;

  congenereOptions: CongenereOption[];
  congenere: string;
  onCongenereChange: (value: string) => void;
  congenereError?: string;

  policyNumber: string;
  onPolicyNumberChange: (value: string) => void;
  policyNumberError?: string;

  disabled?: boolean;
};

export function QuotationDataSection({
  insuranceType,
  onInsuranceTypeChange,
  congenereOptions,
  congenere,
  onCongenereChange,
  congenereError,
  policyNumber,
  onPolicyNumberChange,
  policyNumberError,
  disabled,
}: Props) {
  const isRenewal = insuranceType === 'renewal';

  return (
    <div className='flex flex-col gap-6'>
      <Text className='text-lg font-bold text-alba-black-500'>
        Dados da Cotação
      </Text>

      <div className='flex flex-col gap-2'>
        <Text className='text-sm font-bold text-alba-black-500'>
          Tipo de Seguro
        </Text>
        <RadioCard.Root
          value={insuranceType}
          onValueChange={details =>
            onInsuranceTypeChange(details.value as InsuranceType)
          }
          disabled={disabled}
        >
          <div className='flex gap-2'>
            <RadioCard.Item value='new'>
              <RadioCard.ItemControl className='flex-row items-center gap-2'>
                <FilePlus2 className='w-6 h-6' strokeWidth={1.6} />
                <RadioCard.ItemText>Seguro Novo</RadioCard.ItemText>
              </RadioCard.ItemControl>
              <RadioCard.ItemHiddenInput />
            </RadioCard.Item>
            <RadioCard.Item value='renewal'>
              <RadioCard.ItemControl className='flex-row items-center gap-2'>
                <RefreshCcw className='w-6 h-6' strokeWidth={1.6} />
                <RadioCard.ItemText>Transferência de Congênere</RadioCard.ItemText>
              </RadioCard.ItemControl>
              <RadioCard.ItemHiddenInput />
            </RadioCard.Item>
          </div>
        </RadioCard.Root>
      </div>

      {isRenewal && (
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-10'>
          <RequiredFieldTooltip active>
            <Field.Root invalid={!!congenereError}>
              <Field.Label className='text-sm font-bold text-alba-black-500'>
                Congênere<span className='text-alba-error-red-500'>*</span>
              </Field.Label>
              <Select.Root
                value={congenere ? [congenere] : []}
                onValueChange={details =>
                  onCongenereChange(details.value[0] || '')
                }
                positioning={{ sameWidth: true }}
                items={congenereOptions}
                disabled={disabled}
              >
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder='Selecione a Congênere' />
                    <Select.Indicator />
                  </Select.Trigger>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    {congenereOptions.map(option => (
                      <Select.Item key={option.value} item={option}>
                        <Select.ItemText>{option.label}</Select.ItemText>
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
              {congenereError && (
                <Field.ErrorText>{congenereError}</Field.ErrorText>
              )}
            </Field.Root>
          </RequiredFieldTooltip>

          <Field.Root invalid={!!policyNumberError}>
            <Field.Label className='text-sm font-bold text-alba-black-500'>
              Número da Apólice
            </Field.Label>
            <Field.Input
              value={policyNumber}
              onChange={e => onPolicyNumberChange(e.target.value)}
              placeholder='Digite o número da apólice'
              maxLength={FIELD_MAX_LENGTHS.policyNumber}
              disabled={disabled}
            />
            {policyNumberError && (
              <Field.ErrorText>{policyNumberError}</Field.ErrorText>
            )}
          </Field.Root>
        </div>
      )}
    </div>
  );
}
